<template lang="pug">
.leaderboard-filters
  RogueButton.filter-toggle.outline.black-outline(@click="showFilters = !showFilters") {{ __(showFilters ? 'Hide Filters' : 'Show Filters') }}
  .filters(:class="showFilters ? 'show' : 'hide'")
    //- These are to select an event
    RogueSelect(
      v-if="showTypeYearSeries"
      :options="competitions"
      label="Competition"
      v-model="selectedValues.competition"
      @update:modelValue="updateLeaderboard"
      :class="singleLeaderboard ? 'disabled' : ''"
    )
    RogueSelect(
      v-if="showTypeYearSeries"
      :options="seasons" label="Season"
      v-model="selectedValues.season"
      @update:modelValue="updateLeaderboard"
      :class="singleLeaderboard ? 'disabled' : ''"
    )
    RogueSelect(
      v-if="showTypeYearSeries"
      :options="series"
      label="Series"
      v-model="selectedValues.series"
      @update:modelValue="updateLeaderboard"
      :class="singleLeaderboard ? 'disabled' : ''"
    )
    //- These are to select a division from the event
    RogueSelect(
      v-for="dropdown in dropdownsWithOptions"
      :key="dropdown.label"
      :options="dropdown.options"
      :label="dropdown.label"
      v-model="selectedValues[dropdown.key]"
      @update:modelValue="updateLeaderboard"
    )
    RogueSelect(
      v-if="selectedEvent.showCountryFilter"
      :options="countries"
      label="Country"
      v-model="selectedValues['country']"
      @update:modelValue="updateLeaderboard"
    )
</template>
<script setup lang="ts">
import { onMounted, reactive, ref, computed } from 'vue'
import type { IEvent, IDivision, SelectedValues } from './leaderboard-types'
import { useConfigStore } from '@/js/stores/configStore'

const configStore = useConfigStore()

const emit = defineEmits(['loadLeaderboard'])

const props = withDefaults(defineProps<{
  events?: IEvent[],
  selectedEvent: IEvent,
  selectedDivision: IDivision,
  singleLeaderboard?: boolean,
  defaultEventSeason?: string,
}>(), {
  events             : () => [],
  singleLeaderboard  : false,
  defaultEventSeason : '2026',
})

const selectedValues = reactive<SelectedValues>({
  competition       : '',
  season            : '',
  series            : '',
  teamSize          : '',
  gender            : '',
  scale             : '',
  serviceOccupation : '',
  age               : '',
  weight            : '',
  country           : 'worldwide',
})

const showFilters = ref(false)
const loaded = ref(false)

function uniqueVals(array: string[]) {
  return array.filter((item, index) => item !== undefined && item !== null && array.indexOf(item) === index)
}

const divisionDropdowns = computed(() => {
  const setdropdownLabel = (attribute: string) => {
    const label = attribute.charAt(0).toUpperCase() + attribute.substring(1)

    // Add space before capital letters to handle mult-word attributes
    return label.replace(/([A-Z])/g, ' $1').trim()
  }
  // We start with all divisions for this event.
  // As we apply filters we will narrow down this list to only match the ones that follow the current selections.
  let divisions = props.selectedEvent?.divisions || []

  // This is the order of dropdowns. Each dropdown affects what shows up in the dropdowns after it, so the order is important.
  const divisionAttributes = ['teamSize', 'gender', 'scale', 'weight', 'serviceOccupation', 'age']

  // This is the return value
  const dropdowns:{key: string, label: string, options: {label: string, value: string}[]}[] = []

  // Get all the attributes relevant to the divisions in this event. 
  const attributesForThisEvent = divisionAttributes.filter(attr => {
    return divisions.some(division => attr in division.attributes)
  })

  divisionAttributes.forEach((attribute) => {
    // If this event does not have this filter, we can ignore it
    if (!attributesForThisEvent.includes(attribute)) {
      return
    }

    // Find all possible options for this attribute (e.g. Male / Female)
    const allValuesForThisAttr = divisions.map(division => division.attributes[attribute]).filter(Boolean)
    
    if(!allValuesForThisAttr.length){
      return
    }

    // Since there may be multiple instances of each, we need to get the unique values and tu
    const uniqueDropdownOptions = uniqueVals(allValuesForThisAttr)
      .sort((a, b) => {
        // If these are numbers, sort by the numeric value. This sorts 750 -> 1000 -> 1250
        if (Number.isNaN(Number(a))) {
          a.toString().localeCompare(b.toString())
        }

        return Number(a) - Number(b)
      })
      .map(value => ({ label: value, value }))

    // If there is more than one options, add this dropdown to the UI
    if (uniqueDropdownOptions.length > 1) {
      dropdowns.push({
        key     : attribute,
        label   : setdropdownLabel(attribute),
        options : uniqueDropdownOptions })
    }

    // Check if the customer has already selected an option for this dropdown. If so, the following dropdowns should
    // only show options that are still available
    const currentlySelectedValue = selectedValues[attribute]
    if (currentlySelectedValue !== undefined) {
      divisions = divisions.filter(division => division.attributes[attribute] === currentlySelectedValue)
    }
  })

  return dropdowns
})

const dropdownsWithOptions = computed(() => divisionDropdowns.value.filter(dropdown => dropdown.options.length > 1))

function setSelectedDivisionValues(division?: IDivision) {
  // An event with no divisions configured has nothing to sync the dropdowns to
  if (!division?.attributes) {
    return
  }

  // We need to reset all of the active dropdowns based on the division that we had to pick for them
  Object.keys(division.attributes).forEach(dropdown => {
    // Reset this dropdown to the value for the division we are forcing
    selectedValues[dropdown] = division.attributes[dropdown]
  })
}

function updateLeaderboard() {
  // Find the event that matches what the customer selected (series and season)
  let newEvent = props.events.find((event) => (event.eventType === selectedValues.competition) && (event.name === selectedValues.series) && (`${event.season}` === `${selectedValues.season}`))

  // If we weren't able to find an event, default back to whatever the current one is
  if (!newEvent) {
    // Find all events for the current competition
    newEvent = props.events.find((event) => (event.eventType === selectedValues.competition) && (`${event.season}` === `${selectedValues.season}`))
  }

  if (!newEvent) {
    // We didn't match competition and year, so just check competition
    newEvent = props.events.find((event) => (!!event.eventType && event.eventType === selectedValues.competition))
  }

  if (!newEvent) {
    // Shouldn't get here, but set the default if needed
    newEvent = props.selectedEvent
  }

  // Updating dropdowns to show newEvent
  selectedValues.competition = newEvent.eventType
  selectedValues.season = `${newEvent.season}`
  selectedValues.series = newEvent.name

  // An event may have no divisions configured yet, in which case there is nothing to select
  const newEventDivisions = newEvent.divisions || []

  // Find the division that matches all of the current dropdown values
  let selectedDivision = newEventDivisions
    .find((division: IDivision) => divisionDropdowns.value
      .map(dropdown => dropdown.key)
      .every(divisionAttr => division.attributes[divisionAttr] === selectedValues[divisionAttr]))

  // If we didnt find a matching divisions for all the filters (maybe they changed Event, and the types of divisions no longer match)
  // we want to pick a division for them
  if (selectedDivision === undefined) {
    let divisions = newEventDivisions

    for (const dropdown of divisionDropdowns.value) {
      const matchingDivisions = divisions.filter((division) => division.attributes[dropdown.key] === selectedValues[dropdown.key])
      if (!matchingDivisions.length) {
        break
      }
      divisions = matchingDivisions
    }
    selectedDivision = divisions[0]
  }
  setSelectedDivisionValues(selectedDivision)

  // If the division is different than the currently selected one, we need to tell the parent component by Emitting it
  if (loaded.value) {
    emit('loadLeaderboard', { newEvent, selectedDivision, country: selectedValues.country })
  }
}

onMounted(() => {
  try {
    selectedValues.series = props.selectedEvent.name
    if (props.selectedDivision) {
      setSelectedDivisionValues(props.selectedDivision)
    }
    updateLeaderboard()
  } finally {
    // `loaded` gates the loadLeaderboard emit, so it has to be set even if the initial
    // sync fails - otherwise the dropdowns silently stop changing the leaderboard
    loaded.value = true
  }
})

const showTypeYearSeries = computed(() => !props.selectedEvent.standAloneLeaderboard)

const countries = computed(() => {
  const options = [{ label: 'Worldwide', value: 'worldwide' }]

  options.push(...configStore.countries.map(value => ({ label: value.name, value: value.alpha2 })))

  return options
})

// Dropdown for selecting a competition type
const competitions = computed(() => {
  // Get the valid types of events, removed Record Breakers temporarily
  // TODO: add update to make Record Breakers work for leaderboard
  const types = props.events
    .filter(event => event.eventType !== 'Record Breakers' && event.eventType !== 'CrossFit')
    .map(event => event.eventType)

  // Remove the duplicates
  const uniqueTypes = uniqueVals(types)

  // Return this formatted for dropdowns
  return uniqueTypes.map(type => ({ label: type, value: type }))
})

// Dropdown for selecting a season
const seasons = computed(() => {
  const years = uniqueVals(
    props.events
      .filter(event => event.eventType === selectedValues.competition)
      .map(event => `${event.season}`)
  )

  // Sort years in ascending order, but keep non-numeric values like 'All Years' at the end
  years.sort((a, b) => {
    const aNum = Number(a)
    const bNum = Number(b)
    const aIsNum = !Number.isNaN(aNum)
    const bIsNum = !Number.isNaN(bNum)

    if (aIsNum && bIsNum) return aNum - bNum
    if (aIsNum) return -1
    if (bIsNum) return 1

    return a.localeCompare(b)
  })

  return years.map(year => ({ label: year, value: year }))
})

// Dropdown for selecting a series
const series = computed(() => {
  const options = props.events
    .filter(event => event.season === selectedValues.season && event.eventType === selectedValues.competition)
    .map(event => ({ label: event.name, value: event.name }))
    .filter(event => event.label !== 'Overall')

  if (selectedValues.season !== 'All Years' && selectedValues.competition !== 'The C' && selectedValues.competition !== 'Qualifier') {
    options.unshift({ label: 'Overall', value: 'Overall' })
  }

  return options
})
</script>

<style lang="scss" scoped>
.leaderboard-filters {
  display: block;
  margin: 0 auto;
  width: 100%;

  .filter-toggle {
    display: flex;
    margin-bottom: $space-xs;
  }

  .filters {
    display: none;
    flex-wrap: wrap;
    flex-direction: row;

    &.show {
      display: flex;
    }

    .disabled {
      pointer-events: none;
      --select-background: #{$color-gray};
      --select-color: #{$color-gray-x-dark};
      --select-icon-color: #{$color-gray-x-dark};
    }
  }

  @media (min-width: 1024px) {
    .filter-toggle {
      display: none;
    }

    .filters {
      display: flex;
      flex-wrap: nowrap;

      &.show {
        display: flex;
      }
    }
  }

  .filters .select {
    margin-right: $size-xl;
  }

}
</style>

<style lang="scss" scoped target="app">
// Mobile app-specific styling for native webview
.platform-ios, .platform-android {
  .leaderboard-filters {
    margin-bottom: $space-s;

    .filter-toggle {
      display: flex !important;
      flex: 1;
      --btn-width: 100%;
      --btn-background: linear-gradient(180deg, #f2f2f2 0%, #e0e0e0 50%, #d6d6d6 100%);
      --btn-border: 1px solid #9e9e9e;
      --btn-color: #{$color-black};
      --btn-font-family: #{$font-condensed};
      --btn-font-size: #{$size-s};
      --btn-font-weight: bold;
      --btn-text-transform: uppercase;
      --btn-padding: #{$space-s};
      --btn-border-radius: 6px;
      --btn-box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 1px 1px rgba(0, 0, 0, 0.06);
    }

    .filters {
      background: $color-gray-x-light;
      padding: $space-s;
      margin-top: $space-s;
      border-radius: 4px;
      flex-direction: column;

      &.show {
        display: flex;
      }

      .select {
        margin-right: 0;
        margin-bottom: $space-xs;
        width: 100%;
        --select-wrap-margin: 0;
        --select-font-size: #{$size-xxs};
        --select-padding: #{$space-xs};
        --field-label-font-size: #{$size-xxs};

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}
</style>
